import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import { RolesApiService } from '@angular-workspace/services';

@Component({
  selector: 'app-sandbox',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    LoginModalComponent
  ],
  templateUrl: './sandbox.component.html',
  styleUrl: './sandbox.component.scss',
})
export class SandboxComponent implements OnInit {
  private router = inject(Router);
  private rolesService = inject(RolesApiService);
  private keycloak = inject(KeycloakService);
  
  showLoginModal: boolean = false;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Sandbox' }
    ],
    title: 'Sandbox IDE',
    description: 'A secure environment to explore, test, and integrate APIs, data services, and platform capabilities. A controlled testing space for partners and developers to build and validate agri-data solutions on NHA.'
  };

  features = [
    {
      title: 'Data Exploration',
      description: 'Explore structured and unstructured data.',
      image: '/sandbox/Analyze-cuate.svg'
    },
    {
      title: 'AI Model Training',
      description: 'Build, test, and refine AI models using real datasets.',
      image: '/sandbox/Business Plan-pana.svg'
    },
    {
      title: 'Contextual AI',
      description: 'Refine AI solutions using Telangana-specific datasets',
      image: '/sandbox/Chat bot-amico.svg'
    },
    {
      title: 'Built-In Compute',
      description: 'Access compute directly within your workspace',
      image: '/sandbox/Digital transformation-pana.svg'
    }
  ];

  capabilities = [
    'MLOps and pipeline tools like MLflow, DVC, Weights & Biases, ZenML, Kubeflow, and ClearML.',
    'Data processing and feature engineering libraries such as Dask, Vaex, Featuretools, and Feast.',
    'Visualization and dashboarding tools including Plotly, Bokeh, HoloViews, and Streamlit.',
    'Model serving and deployment frameworks like BentoML, Seldon Core, TorchServe, and Gradio.',
    'Articles & Guides: Learn from practical notes, tutorials, and framework walkthroughs.',
    'Video Tutorials: Leverage platform tutorials to effectively understand and utilize NHA resources'
  ];

  async ngOnInit(): Promise<void> {
    // Ensure roles are loaded
    try {
      await this.rolesService.getRoles();
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }

  /**
   * Handle Create New Notebook button click
   * Check if user is logged in, show login modal if not, otherwise navigate to My Projects page based on user's highest role
   */
  async onCreateNotebookClick(): Promise<void> {
    try {
      // Check if user is logged in
      let isLoggedIn = false;
      try {
        // Give Keycloak a moment to initialize if needed
        await new Promise(resolve => setTimeout(resolve, 100));
        isLoggedIn = await this.keycloak.isLoggedIn();
      } catch (error) {
        console.log('Keycloak.isLoggedIn() failed, trying alternative check');
        // Fallback: Check if we can get the token from Keycloak
        try {
          const token = await this.keycloak.getToken();
          isLoggedIn = !!token;
        } catch (e) {
          // If that fails, check localStorage
          const localToken = localStorage.getItem('ai-sandbox-cat-auth-token');
          isLoggedIn = !!localToken;
        }
      }

      // If not logged in, show login modal
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }

      // User is logged in, proceed with navigation
      // Get user roles
      const userRoles = this.rolesService.getUserRoles();
      
      if (!userRoles || userRoles.length === 0) {
        console.warn('No user roles found, defaulting to consumer');
        this.router.navigate(['/consumer/my-projects']);
        return;
      }

      // Get the highest role
      const highestRole = this.rolesService.getHighestRole(userRoles);
      
      if (!highestRole) {
        console.warn('Could not determine highest role, defaulting to consumer');
        this.router.navigate(['/consumer/my-projects']);
        return;
      }

      // Map role to route
      // Normalize role (handle both 'org-admin' and 'org_admin' formats)
      const normalizedRole = highestRole.toLowerCase().replace(/_/g, '-');
      
      let route: string;
      switch (normalizedRole) {
        case 'admin':
        case 'cos-admin':
        case 'platform-admin':
          // Platform admin uses /admin/project (based on ui-tgdex routes)
          route = '/admin/project';
          break;
        case 'org-admin':
          route = '/org-admin/my-projects';
          break;
        case 'provider':
          route = '/provider/my-projects';
          break;
        case 'consumer':
          route = '/consumer/my-projects';
          break;
        default:
          console.warn(`Unknown role: ${highestRole}, defaulting to consumer`);
          route = '/consumer/my-projects';
      }

      console.log(`Navigating to My Projects for role ${highestRole}: ${route}`);
      this.router.navigate([route]);
    } catch (error) {
      console.error('Error navigating to My Projects:', error);
      // Fallback to consumer route on error
      this.router.navigate(['/consumer/my-projects']);
    }
  }
}
